import type { AddMemberBody, CreateProjectBody, Member, Project, UpdateProjectBody } from '@tasks/contracts';

import type { ProjectRole } from '../lib/types';
import { apiRequest, apiRequestList, apiRequestResource, apiRequestVoid } from './client';

export type ArchivedFilter = 'true' | 'false' | 'all';

export function listProjects(archived: ArchivedFilter = 'false', signal?: AbortSignal): Promise<Project[]> {
  return apiRequestList<Project>('/projects', { query: { archived }, signal });
}

export function getProject(key: string, signal?: AbortSignal): Promise<Project> {
  return apiRequest<Project>(`/projects/${encodeURIComponent(key)}`, { signal });
}

export function createProject(body: CreateProjectBody): Promise<Project> {
  return apiRequestResource<Project>('/projects', 'project', { method: 'POST', body });
}

export function updateProject(key: string, body: UpdateProjectBody): Promise<Project> {
  return apiRequest<Project>(`/projects/${encodeURIComponent(key)}`, { method: 'PATCH', body });
}

export function archiveProject(key: string): Promise<void> {
  return apiRequestVoid(`/projects/${encodeURIComponent(key)}/archive`, { method: 'POST' });
}

export function unarchiveProject(key: string): Promise<void> {
  return apiRequestVoid(`/projects/${encodeURIComponent(key)}/unarchive`, { method: 'POST' });
}

/** Удаление требует подтверждения ключом (docs/05 §4). */
export function deleteProject(key: string, confirmKey: string): Promise<void> {
  return apiRequestVoid(`/projects/${encodeURIComponent(key)}`, {
    method: 'DELETE',
    query: { confirm: confirmKey },
  });
}

export function listMembers(key: string, signal?: AbortSignal): Promise<Member[]> {
  return apiRequestList<Member>(`/projects/${encodeURIComponent(key)}/members`, { signal });
}

/**
 * Сервер отвечает полным списком участников (`{ items }`), а не одним
 * участником, — клиент возвращает список (docs/05 §4).
 */
export function addMember(key: string, body: AddMemberBody): Promise<Member[]> {
  return apiRequestList<Member>(`/projects/${encodeURIComponent(key)}/members`, { method: 'POST', body });
}

/** Сервер отвечает `204 No Content`, поэтому тело не читаем. */
export function updateMemberRole(key: string, userId: string, role: ProjectRole): Promise<void> {
  return apiRequestVoid(`/projects/${encodeURIComponent(key)}/members/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    body: { role },
  });
}

/** `reassign=keep` оставляет задачи на ушедшем, `null` — снимает назначение. */
export function removeMember(key: string, userId: string, reassign: 'keep' | 'null'): Promise<void> {
  return apiRequestVoid(`/projects/${encodeURIComponent(key)}/members/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
    query: { reassign },
  });
}
